/**
 * Inicialización de Sentry (browser + replay + tracing).
 *
 * Variables Vite: VITE_SENTRY_DSN, VITE_APP_ENV, VITE_APP_RELEASE, etc.
 * En Create React App (este repo hoy): define los mismos valores con prefijo REACT_APP_
 * (ver mapa CRA_MAP abajo) porque CRA no expone import.meta.env ni variables que no empiecen por REACT_APP_.
 */
import * as Sentry from "@sentry/react";

/**
 * DSN público del navegador (no es secreto). Prioridad: VITE_SENTRY_DSN / REACT_APP_SENTRY_DSN.
 * Para otro proyecto o desactivar envíos, define REACT_APP_SENTRY_DSN vacío y ajusta esta constante.
 */
const RESERVEO_PUBLIC_SENTRY_DSN =
  "https://7b0221daf94b7a605b27e8973f53d8b0@o4505440201867264.ingest.us.sentry.io/4511238115491840";

const CRA_MAP = {
  VITE_SENTRY_DSN: "REACT_APP_SENTRY_DSN",
  VITE_APP_ENV: "REACT_APP_ENV",
  VITE_APP_RELEASE: "REACT_APP_RELEASE",
  VITE_SENTRY_TRACES_SAMPLE_RATE: "REACT_APP_SENTRY_TRACES_SAMPLE_RATE",
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: "REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
  VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: "REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE",
  VITE_BACKEND_ORIGIN: "REACT_APP_BACKEND_URL",
};

/** @param {keyof typeof CRA_MAP} key */
function readEnv(key) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env && key in import.meta.env) {
      const v = import.meta.env[key];
      if (v !== undefined && v !== "") return String(v);
    }
  } catch {
    // bundler sin import.meta
  }
  const craKey = CRA_MAP[key];
  if (craKey && typeof process !== "undefined" && process.env && process.env[craKey]) {
    return String(process.env[craKey]);
  }
  return "";
}

function resolveSentryDsn() {
  const fromEnv = readEnv("VITE_SENTRY_DSN");
  if (fromEnv) return fromEnv;
  return RESERVEO_PUBLIC_SENTRY_DSN;
}

const SENSITIVE_KEY = /password|passwd|token|secret|authorization|cookie|jwt|apikey|api_key|bearer|refresh|credit|cvv|pin/i;

function sanitizeKey(key) {
  return SENSITIVE_KEY.test(String(key));
}

/** @param {unknown} value */
function sanitizeValue(value) {
  if (value == null) return value;
  if (typeof value === "string") {
    return value.length > 2000 ? `${value.slice(0, 2000)}…` : value;
  }
  if (Array.isArray(value)) return value.map((v) => (typeof v === "object" ? sanitizeObject(v) : v));
  if (typeof value === "object") return sanitizeObject(value);
  return value;
}

/** @param {Record<string, unknown> | null | undefined} obj */
function sanitizeObject(obj) {
  if (!obj || typeof obj !== "object") return obj;
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (sanitizeKey(k)) {
      out[k] = "[REDACTED]";
      continue;
    }
    if (v && typeof v === "object") out[k] = sanitizeObject(/** @type {Record<string, unknown>} */ (v));
    else out[k] = sanitizeValue(v);
  }
  return out;
}

function tracePropagationTargets() {
  const backend = readEnv("VITE_BACKEND_ORIGIN");
  if (!backend) return ["localhost", /^\/(api|v1)/];
  try {
    const u = new URL(backend.startsWith("http") ? backend : `https://${backend}`);
    return ["localhost", u.origin, new RegExp(`^${u.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`)];
  } catch {
    return ["localhost"];
  }
}

function parseRate(key, fallback) {
  const raw = readEnv(key);
  if (raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;
}

let sentryInitialized = false;

export function initSentry() {
  const dsn = resolveSentryDsn();
  const enabled = Boolean(dsn);

  if (!enabled || sentryInitialized) {
    return;
  }
  sentryInitialized = true;

  const environment = readEnv("VITE_APP_ENV") || process.env.NODE_ENV || "development";
  const release = readEnv("VITE_APP_RELEASE") || undefined;

  Sentry.init({
    dsn,
    enabled,
    environment,
    release,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: tracePropagationTargets(),
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: parseRate("VITE_SENTRY_TRACES_SAMPLE_RATE", 0.1),
    replaysSessionSampleRate: parseRate("VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE", 0.1),
    replaysOnErrorSampleRate: parseRate("VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE", 1),
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data && typeof breadcrumb.data === "object") {
        return { ...breadcrumb, data: sanitizeObject(/** @type {Record<string, unknown>} */ (breadcrumb.data)) };
      }
      return breadcrumb;
    },
    beforeSend(event) {
      try {
        if (event.request && event.request.headers) {
          const h = { ...event.request.headers };
          for (const k of Object.keys(h)) {
            if (sanitizeKey(k)) h[k] = "[REDACTED]";
          }
          event.request.headers = h;
        }
        if (event.extra) event.extra = sanitizeObject(/** @type {Record<string, unknown>} */ (event.extra));
        if (event.contexts) event.contexts = sanitizeObject(/** @type {Record<string, unknown>} */ (event.contexts));
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((b) =>
            b.data && typeof b.data === "object"
              ? { ...b, data: sanitizeObject(/** @type {Record<string, unknown>} */ (b.data)) }
              : b
          );
        }
      } catch {
        // no bloquear envío si el saneo falla
      }
      return event;
    },
  });

  Sentry.setTag("runtime", "react");
  if (typeof window !== "undefined") {
    Sentry.setContext("page", {
      path: `${window.location.pathname}${window.location.search}`,
      referrer: document.referrer || undefined,
    });
  }
}

/** @returns {{ ok: true, eventId?: string } | { ok: false, reason: string }} */
export function sendSentryLoginSmokeTest() {
  if (!Sentry.getClient()) {
    return { ok: false, reason: "Sentry no está inicializado (sin cliente)." };
  }
  const eventId = Sentry.captureMessage("Prueba manual desde Login — Reserveo", {
    level: "info",
    tags: { source: "login_smoke_test" },
    contexts: {
      smoke_test: {
        when: new Date().toISOString(),
        path: typeof window !== "undefined" ? window.location.pathname : "",
      },
    },
  });
  return { ok: true, eventId: eventId ?? undefined };
}

initSentry();
